import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button, Field, Input, Modal, Select, Toast } from '@/components/ui';
import { dialerOptions, useDialers } from '@/lib/dialers';

interface Result {
  created: string[];
  skipped: string[];
  created_count: number;
  skipped_count: number;
  with_password: boolean;
  note: string | null;
}

/**
 * Create a numeric range of Vicidial agents.
 *
 * Dialers number their Remote Agent users consecutively — 13001 to 13010 — so
 * the common case is ten near-identical forms. Importing from the dialer covers
 * it when the dialer is reachable and live; this covers when it is not, and it
 * is the only option while a server is still in mock mode, where an import
 * would invent agents rather than find yours.
 */
export function BulkVicidialUsers({ onDone }: { onDone?: () => void }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const [start, setStart] = useState('13001');
  const [end, setEnd] = useState('13010');
  const [prefix, setPrefix] = useState('');
  const [namePrefix, setNamePrefix] = useState('Agent');
  const [campaign, setCampaign] = useState('');
  const [inGroup, setInGroup] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [password, setPassword] = useState('');
  const [sameAsUsername, setSameAsUsername] = useState(false);

  const { data: dialers } = useDialers();
  const servers = dialerOptions(dialers?.data);

  const create = useMutation({
    mutationFn: () =>
      api.post<Result>('/vicidial-users/bulk', {
        start: Number(start),
        end: Number(end),
        prefix: prefix || undefined,
        name_prefix: namePrefix || undefined,
        campaign: campaign || undefined,
        in_group: inGroup || undefined,
        integration_id: integrationId || undefined,
        password: sameAsUsername ? undefined : password || undefined,
        password_same_as_username: sameAsUsername || undefined,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-users'] });
      setOpen(false);
      onDone?.();
      const summary = result.skipped_count
        ? `Created ${result.created_count}. Skipped ${result.skipped_count} that already existed.`
        : `Created ${result.created_count} agent${result.created_count === 1 ? '' : 's'}.`;
      setToast({
        // A missing password is not an error, but it does mean these agents
        // cannot log in — so it is said plainly rather than left to be found.
        message: result.note ? `${summary} ${result.note}` : summary,
        tone: result.note ? 'bad' : 'good',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not create the agents'), tone: 'bad' }),
  });

  const from = Number(start);
  const to = Number(end);
  const count = Number.isFinite(from) && Number.isFinite(to) && to >= from ? to - from + 1 : 0;
  const sample = count
    ? [`${prefix}${from}`, count > 2 ? '…' : null, count > 1 ? `${prefix}${to}` : null]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Bulk add agents
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Bulk add Vicidial agents"
        description="Creates a consecutive range, the way dialers usually number Remote Agent users."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={count < 1 || count > 500}
              onClick={() => create.mutate()}
            >
              {count ? `Create ${count} agent${count === 1 ? '' : 's'}` : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {servers.length > 0 && (
            <Field
              label="Vicidial server"
              required
              hint="Which dialer these agents log into. This is what routes their calls' dispositions back."
            >
              <Select value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}>
                <option value="">Select…</option>
                {servers.map((server) => (
                  <option key={server.value} value={server.value}>
                    {server.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="From" required>
              <Input
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                inputMode="numeric"
              />
            </Field>
            <Field label="To" required>
              <Input
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                inputMode="numeric"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Username prefix"
              hint="Leave blank for plain numbers like 13001."
            >
              <Input
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="(none)"
              />
            </Field>
            <Field label="Display name prefix">
              <Input
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="Agent"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Campaign" hint="Optional.">
              <Input
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="HOME01"
              />
            </Field>
            <Field label="In-group" hint="Optional.">
              <Input
                value={inGroup}
                onChange={(e) => setInGroup(e.target.value)}
                placeholder="VERIFIER01"
              />
            </Field>
          </div>

          <div>
            <Field
              label="Vicidial password"
              hint="Each agent's own password on the dialer. Without one they cannot log in, and no call reaches their bot."
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={sameAsUsername ? 'using the username' : 'same password for every agent'}
                disabled={sameAsUsername}
                autoComplete="new-password"
              />
            </Field>
            <label className="mt-2 flex items-start gap-2 text-sm text-ink-700 dark:text-ink-200">
              <input
                type="checkbox"
                checked={sameAsUsername}
                onChange={(e) => setSameAsUsername(e.target.checked)}
                className="mt-0.5 rounded border-ink-300"
              />
              <span>
                Use each username as its password
                <span className="block text-xs text-ink-500 dark:text-ink-400">
                  How many dialers are provisioned. Weak, so it is a choice rather
                  than a default.
                </span>
              </span>
            </label>
          </div>

          {/* Show what will actually be created. A prefix plus a range is easy
              to get subtly wrong, and the names are matched against Vicidial
              exactly. */}
          {count > 0 && count <= 500 ? (
            <p className="rounded-lg border border-line bg-sunken px-3 py-2 text-xs text-ink-600 dark:text-ink-300">
              Will create <strong>{count}</strong> agent{count === 1 ? '' : 's'}:{' '}
              <code className="font-mono">{sample}</code>. Names must match Vicidial
              exactly. Any that already exist are skipped.
            </p>
          ) : (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              {count > 500
                ? `That range is ${count} agents. Create at most 500 at a time.`
                : 'Enter a range — the end must not be before the start.'}
            </p>
          )}
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
